/**
 * Hook to check whether the Rovo Insights feature is available for the current user.
 *
 * Uses RovoInsightsService (extends AssistanceServiceBase) so that Rovo Insights
 * concerns stay out of the general-purpose AssistanceService.
 */
import { useMemo } from 'react';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import type { InsightsStatusResponse } from '@atlassian/rovo-insights-service-api/types';
import { RovoInsightsService } from '@atlassian/rovo-insights-service/service';

import type { InsightsStatusFetchState } from '../../common/insights-banner-visibility';

const FRESHNESS_WINDOW_MS = 48 * 60 * 60 * 1000;

const isGeneratedAtFresh = (generatedAt: string | undefined): boolean => {
	if (!generatedAt) {
		return false;
	}

	const generatedAtTime = new Date(generatedAt).getTime();
	if (Number.isNaN(generatedAtTime)) {
		return false;
	}

	return Date.now() - generatedAtTime < FRESHNESS_WINDOW_MS;
};

export interface UseInsightsStatusResult {
	isInsightsAvailable: boolean;
	isLoading: boolean;
	/** UTC ISO-8601 timestamp when the current insights were generated */
	generatedAt?: string;
	/**
	 * True when the generated timestamp is inside the 48-hour freshness window.
	 * Missing or invalid timestamps are treated as stale so Quick Actions does not
	 * keep showing insights without a reliable generation time.
	 */
	isFresh: boolean;
	/**
	 * Settled state of the status fetch. Lets callers distinguish a genuine negative
	 * (`success` + not available) from a fetch failure (`error`) — otherwise both
	 * collapse into `isInsightsAvailable: false` and become indistinguishable in telemetry.
	 */
	statusFetchState: InsightsStatusFetchState;
}

interface UseInsightsStatusProps extends AssistanceServiceInjectedParams {
	enabled: boolean;
}

export const useInsightsStatus: (
	props: Omit<UseInsightsStatusProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => UseInsightsStatusResult = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { assistanceService, experienceTracker },
		enabled,
	}: UseInsightsStatusProps) => {
		const insightsService = useMemo(() => {
			const config = assistanceService.getConfig();
			return new RovoInsightsService(config);
		}, [assistanceService]);

		const { data, isFetching, error } = useQuery<InsightsStatusResponse>({
			queryKey: ['insightsStatus'],
			enabled,
			queryFn: async () => {
				experienceTracker.start({
					id: ExperienceName.FETCH_INSIGHTS_STATUS,
					name: ExperienceName.FETCH_INSIGHTS_STATUS,
				});
				try {
					const response = await insightsService.getInsightsStatus();
					experienceTracker.succeed({
						name: ExperienceName.FETCH_INSIGHTS_STATUS,
					});
					return response;
				} catch (error) {
					experienceTracker.fail({
						teamName: 'Rovo Expansion',
						name: ExperienceName.FETCH_INSIGHTS_STATUS,
						error: error instanceof Error ? error : new Error(String(error)),
					});
					throw error;
				}
			},
		});

		// `success` means the fetch settled with data; a null `data` (no error) is still
		// pending, regardless of the transient `isFetching` flag — otherwise a no-data
		// window could mislabel as `success` and lock in a wrong reason via the fire-once ref.
		const statusFetchState: InsightsStatusFetchState = !enabled
			? 'disabled'
			: error
				? 'error'
				: data === null
					? 'loading'
					: 'success';

		return {
			isInsightsAvailable: Boolean(data?.insights_available),
			isLoading: isFetching && data === null,
			generatedAt: data?.generated_at,
			isFresh: isGeneratedAtFresh(data?.generated_at),
			statusFetchState,
		};
	},
);
